// hooks/useTreeControlFlags.ts
import { useMemo } from 'react';
import { isAdministrator } from 'utils/Global';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import { isPasteAllowedForRootNodes } from './TreeControlHeader/utils';
import { AllowedTypesForOperation } from './TreeControlHeader/TreeControlHeader';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';

export type TreeControlFlags = {
  isAddNotAllowed: boolean;
  isEditAllowed: boolean;
  copyDisabled: boolean;
  pasteDisabled: boolean;
  cutDisabled: boolean;
  isControlsDisabled: boolean;
  isAnySelectedNodeLocked: boolean;
  canModifyNode: boolean;
};

export default function useTreeControlFlags(
  treeControlState,
  nodesToCopy,
  newFolderType,
): TreeControlFlags {
  const selectedNode = treeControlState.selectedNodes?.[0];
  const selectedExplorerNode = treeControlState.selectedExplorerNode;

  return useMemo(() => {
    const isAnySelectedNodeLocked = treeControlState.selectedNodes.some(
      (node) => node.locked,
    );

    const isEditAllowed = selectedNode
      ? selectedNode.allowEdits
      : selectedExplorerNode?.allowEdits;

    const canModifyNode =
      selectedExplorerNode?.accessRightName === ETreeNodeAccessRightNames.Modify ||
      isAdministrator();

    const isControlsDisabled = !treeControlState.selectedNodes?.some((node) =>
      AllowedTypesForOperation.includes(node.type),
    );

    const isAddNotAllowed =
      newFolderType == ETreeNodeTypes.ProjectsFolder ||
      newFolderType == ETreeNodeTypes.Project ||
      newFolderType == ETreeNodeTypes.Screens ||
      newFolderType == ETreeNodeTypes.ScreensFolder ||
      newFolderType == ETreeNodeTypes.SynchrotronTripv4Folder ||
      newFolderType == ETreeNodeTypes.SearchesFolder;

    const copyDisabled = isControlsDisabled || isAnySelectedNodeLocked;

    const pasteAllowedForRoot = isPasteAllowedForRootNodes(treeControlState, nodesToCopy);

    const pasteDisabled =
      !isEditAllowed ||
      !canModifyNode ||
      (isControlsDisabled && !pasteAllowedForRoot) ||
      isAnySelectedNodeLocked ||
      nodesToCopy.length === 0;

    const cutDisabled =
      !isEditAllowed || !canModifyNode || isControlsDisabled || isAnySelectedNodeLocked;

    return {
      isAnySelectedNodeLocked,
      isEditAllowed,
      canModifyNode,
      isControlsDisabled,
      isAddNotAllowed,
      copyDisabled,
      pasteDisabled,
      cutDisabled,
    };
  }, [treeControlState, nodesToCopy, newFolderType]);
}
